import React, { useState, useEffect, useContext } from 'react';
import { PlayIcon, PauseIcon, SpeakerHighIcon, SpeakerXIcon, SubtitlesIcon, SubtitlesSlashIcon, ChatCenteredSlashIcon, SquareHalfIcon, SquareHalfBottomIcon, GearSixIcon, ChatCenteredIcon, CaretDownIcon, CameraIcon, StopIcon, QueueIcon, InfoIcon, FilmReelIcon } from '@phosphor-icons/react';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, DropdownSection } from '@heroui/react';
import { FeaturesContext, StatsContext } from '@/app/contexts';
import { recordStat, formatTimeIndex } from '@/shared/functions';
import { MediaObject } from '@/shared/types';
import { Tooltip } from '@heroui/tooltip';
// TODO: store times in mediaContext to use for "displayPosition", and start/end clip #t=17.79,25.12

interface ControlsProps {
  isPlaying: boolean;
  togglePlay: () => void;
  isMuted: boolean;
  toggleMute: () => void;
  volume: number;
  handleVolumeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  currentTime: string;
  duration: number;
  isMenuVisible: boolean;
  toggleMenuVisibility: () => void;
  playbackRate: number;
  handlePlaybackRateChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  showTranscript: boolean;
  toggleTranscriptVisibility: () => void;
  showTranscriptMarkers: boolean;
  toggleTranscriptMarkersVisibility: () => void;
  transcriptAvailable: boolean;
  transcriptText?: string;
  transcriptSrc?: string;
  menuRef: React.RefObject<HTMLDivElement>;
  changeTranscriptPosition: ((position: 'side' | 'below') => void) | null;
  currentPosition: 'side' | 'below';
  setStillFrame: () => void;
  setStartTime: () => void;
  setEndTime: () => void;
  mediaItem: MediaObject;
  autoScroll: boolean;
  toggleAutoScroll: () => void;
  updateMediaContext: (key: string, value: any) => void;
  setIsEditingMediaContext: (isEditingMediaContext: boolean) => void;
  isEditingMediaContext: boolean;
  isEditing: boolean;
}
const Controls: React.FC<ControlsProps> = ({
  isPlaying,
  togglePlay,
  isMuted,
  toggleMute,
  volume,
  handleVolumeChange,
  currentTime,
  duration,
  isMenuVisible,
  toggleMenuVisibility,
  playbackRate,
  handlePlaybackRateChange,
  showTranscript,
  toggleTranscriptVisibility,
  showTranscriptMarkers,
  toggleTranscriptMarkersVisibility,
  transcriptAvailable,
  transcriptText,
  transcriptSrc,
  menuRef,
  changeTranscriptPosition,
  currentPosition,
  setStillFrame,
  setStartTime,
  setEndTime,
  mediaItem,
  autoScroll,
  toggleAutoScroll,
  updateMediaContext,
  setIsEditingMediaContext,
  isEditingMediaContext,
  isEditing
}) => {
  const statsContext = useContext(StatsContext);
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const [isMenuVisibleState, setIsMenuVisible] = useState(false);
  const [showTranscriptMarkersState, setShowTranscriptMarkersState] = useState(false);
  useEffect(() => {
    setShowTranscriptMarkersState(showTranscriptMarkers);
  }, [showTranscriptMarkers]);
  const handleToggleMenuVisibility = () => {
    setIsMenuVisible(!isMenuVisibleState);
    toggleMenuVisibility();
  };
  const handleToggleTranscriptMarkers = () => {
    setShowTranscriptMarkersState(!showTranscriptMarkersState);
    toggleTranscriptMarkersVisibility();
  };
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore hotkeys if any input element or markdown editor has focus
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement || event.target instanceof HTMLElement && event.target.closest('.mdxeditor-root-contenteditable') || event.target instanceof HTMLElement && event.target.closest('#feedback-button')) {
        return;
      }
      if (event.key === 't') {
        toggleTranscriptVisibility();
      } else if (event.key === 'm') {
        handleToggleTranscriptMarkers();
      } else if (event.key === 'p' && !!changeTranscriptPosition) {
        changeTranscriptPosition(currentPosition === 'side' ? 'below' : 'side');
      } else if (event.key === 'a') {
        toggleAutoScroll();
      } else if (event.key === 'e') {
        // Toggle edit mode
        if (updateMediaContext && !isEditingMediaContext) {
          // Initialize mediaContext if it doesn't exist
          if (!mediaItem.mediaContext) {
            updateMediaContext('mediaContext', {});
          }

          // Store original times when entering edit mode
          const originalStartTime = mediaItem.mediaContext?.startTime !== undefined ? mediaItem.mediaContext.startTime : mediaItem.mediaContext?.sourceContext?.startTime || 0;
          const originalEndTime = mediaItem.mediaContext?.endTime !== undefined ? mediaItem.mediaContext.endTime : duration;
          updateMediaContext('originalStartTime', originalStartTime);
          updateMediaContext('originalEndTime', originalEndTime);

          // Initialize new start and end times
          // If there are existing mediaContext times, use those; otherwise start fresh
          updateMediaContext('startTime', mediaItem.mediaContext?.startTime !== undefined ? mediaItem.mediaContext.startTime : 0);
          updateMediaContext('endTime', mediaItem.mediaContext?.endTime !== undefined ? mediaItem.mediaContext.endTime : duration);
        } else {
          setIsEditingMediaContext(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentPosition, toggleTranscriptVisibility, handleToggleTranscriptMarkers, changeTranscriptPosition, toggleAutoScroll, updateMediaContext, mediaItem.mediaContext?.startTime, mediaItem.mediaContext?.endTime, isEditingMediaContext, duration]);
  const handleSetStillFrame = () => {
    setStillFrame();
  };
  const handleSetStartTime = () => {
    setStartTime();
  };
  const handleSetEndTime = () => {
    setEndTime();
  };
  const handleSave = () => {
    if (updateMediaContext) {
      // Convert to numbers before saving if they're strings
      if (mediaItem.mediaContext?.startTime) {
        updateMediaContext('startTime', Number(mediaItem.mediaContext.startTime));
      }
      if (mediaItem.mediaContext?.endTime) {
        updateMediaContext('endTime', Number(mediaItem.mediaContext.endTime));
      }
      setIsEditingMediaContext(false);
    }
  };
  const handleCancel = () => {
    if (updateMediaContext) {
      // Reset start and end times to their original values
      updateMediaContext('startTime', mediaItem.mediaContext?.originalStartTime);
      updateMediaContext('endTime', mediaItem.mediaContext?.originalEndTime);
      setIsEditingMediaContext(false);
    }
  };
  const handleEditModeToggle = () => {
    if (updateMediaContext) {
      if (!isEditingMediaContext) {
        // Initialize mediaContext if it doesn't exist
        if (!mediaItem.mediaContext) {
          updateMediaContext('mediaContext', {});
        }

        // Store original times when entering edit mode
        const originalStartTime = mediaItem.mediaContext?.startTime !== undefined ? mediaItem.mediaContext.startTime : mediaItem.mediaContext?.sourceContext?.startTime || 0;
        const originalEndTime = mediaItem.mediaContext?.endTime !== undefined ? mediaItem.mediaContext.endTime : duration;
        updateMediaContext('originalStartTime', originalStartTime);
        updateMediaContext('originalEndTime', originalEndTime);

        // Initialize new start and end times
        // If there are existing mediaContext times, use those; otherwise start fresh
        updateMediaContext('startTime', mediaItem.mediaContext?.startTime !== undefined ? mediaItem.mediaContext.startTime : 0);
        updateMediaContext('endTime', mediaItem.mediaContext?.endTime !== undefined ? mediaItem.mediaContext.endTime : duration);
        setIsEditingMediaContext(true);
      } else {
        // Exiting edit mode
        setIsEditingMediaContext(false);
      }
    }
  };
  const handleRestoreOriginalTimes = () => {
    if (updateMediaContext && mediaItem.mediaContext?.originalStartTime !== undefined) {
      updateMediaContext('startTime', mediaItem.mediaContext.originalStartTime);
      updateMediaContext('endTime', mediaItem.mediaContext.originalEndTime);
    }
  };
  return <div className="flex items-center justify-between px-4 pb-4" data-sentry-component="Controls" data-sentry-source-file="controls.tsx">
      {/* Playback Controls */}
      <div className="flex items-center">
        <button data-testid="PlayPauseButton" aria-label={isPlaying ? "Pause" : "Play"} onClick={event => {
        togglePlay();
        recordStat('PlayPauseButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
      }} className="relative py-1 px-1.5 rounded-md bg-gray-700 hover:bg-gray-400 transition items-center justify-center cursor-pointer text-white hover:text-gray-700 z-10 border-gray-700 border-[0.5px] border-solid">
          {isPlaying ? <PauseIcon weight="fill" className="w-6 h-6 mt-0.5" /> : <PlayIcon weight="fill" className="w-6 h-6 mt-0.5" />}
        </button>
        <div className="flex items-center gap-1 relative text-sm text-gray-300 rounded-r-sm bg-white/5 pr-1 py-[2px] ml-[-18px] mt-[1px] pl-[24px] z-0 min-w-[80px] border-gray-700 border-[0.5px] border-solid">
          <span className="text-white/80 font-light tracking-wider min-w-[35px] text-right">{currentTime}</span>
          <span className="text-white/20 font-semibold text-lg">/</span>
          <span className="text-white/80 min-w-[35px] font-light tracking-wider">{formatTimeIndex(duration.toString())}</span>
          {!isEditingMediaContext && <Dropdown placement='right' className='w-[50px]' classNames={{
          base: 'flex flex-col text-white/80 whitespace-nowrap outline-none w-fit m-0 p-0',
          content: 'bg-transparent p-0 m-0 shadow-none'
        }}>
              <DropdownTrigger className='tool-trigger' data-state={isMenuVisibleState ? 'selected' : ''}>
                <div className="flex flex-row items-center justify-center gap-1 px-1 py-0.5 text-white/80 bg-white/0 hover:text-white hover:bg-gray-900 rounded-sm outline-none cursor-pointer border-1 border-white/15 border-solid ml-[7px] transition-all duration-300">
                  <span>{playbackRate}x</span><CaretDownIcon size={11} className='mt-[2px]' />
                </div>
              </DropdownTrigger>
              <DropdownMenu classNames={{
            base: 'p-0 m-0',
            list: 'bg-gray-900 rounded-sm m-0 p-1 pr-2 text-white/80 whitespace-nowrap outline-none w-[35px]'
          }}>
                {['0.5', '1', '1.5', '2'].map(rate => <DropdownItem key={rate} onPress={() => {
              handlePlaybackRateChange({
                target: {
                  value: rate
                }
              } as React.ChangeEvent<HTMLSelectElement>);
              handleToggleMenuVisibility();
            }} className="tool-item">
                    {rate}x
                  </DropdownItem>)}
              </DropdownMenu>
            </Dropdown>}
        </div>
      </div>
      {/* Right side controls */}
      <div className="flex flex-1 items-center justify-end gap-1.5">
        {isEditingMediaContext ? <>
            <button onClick={handleSave} className="px-3 py-1 rounded-md bg-green-500 hover:bg-green-700 transition items-center justify-center cursor-pointer text-white hover:text-white z-10 flex gap-1">
              <span>Save</span>
            </button>
          </> : <>
            {/* Volume */}
            <div className="hidden sm:flex items-center">
              <button aria-label={isMuted ? "Unmute" : "Mute"} onClick={toggleMute} className="px-1 py-0.5 rounded-sm text-white hover:text-gray-500 transition duration-300 cursor-pointer">
                {isMuted ? <SpeakerXIcon weight="fill" className="w-5 h-5 mt-0.5" /> : <SpeakerHighIcon weight="fill" className="w-5 h-5 mt-0.5" />}
              </button>
              <div className="flex items-center relative w-12 h-1.5 mt-[-4.5px]">
                <input type="range" min={0} max={1} step={0.1} value={isMuted ? 0 : volume} onChange={handleVolumeChange} aria-label="Volume" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((isMuted ? 0 : volume) * 100)} className="absolute inset-0 w-full h-full bg-gray-900 rounded appearance-none cursor-pointer range-sm outline-none volume-slider" />
                <div className="absolute top-[2px] left-[1px] h-1.5 bg-gradient-to-r from-white/50 via-white/70 to-white/80 rounded-l-full" style={{
              width: `calc(${isMuted ? 0 : volume * 100}%)`
            }} aria-hidden="true"></div>
              </div>  
            </div>
            {/* Settings Menu */}
            {(transcriptSrc || mediaItem.mediaType === 'video') && <Dropdown placement='bottom-end' classNames={{
          base: 'flex flex-col text-white/80 whitespace-nowrap outline-none w-fit m-0 p-0',
          content: 'bg-gray-900 rounded-md m-0 p-1 pb-0'
        }}>
                <DropdownTrigger aria-label="Media settings menu" className='tool-trigger hover:text-gray-500' data-state={isMenuVisibleState ? 'selected' : ''}>
                  <GearSixIcon size={25} />
                </DropdownTrigger>
                <DropdownMenu classNames={{
            base: 'p-0 m-0',
            list: 'bg-gray-900 rounded-sm m-0 p-0 text-white/80 whitespace-nowrap outline-none'
          }} disabledKeys={!changeTranscriptPosition ? ["transcript-position"] : []} onAction={e => {
            if (e === 'transcript-visibility') {
              toggleTranscriptVisibility();
            } else if (e === 'transcript-position') {
              if (!!changeTranscriptPosition) {
                changeTranscriptPosition(currentPosition === 'side' ? 'below' : 'side');
              }
            } else if (e === 'auto-scroll') {
              toggleAutoScroll();
            } else if (e === 'transcript-markers') {
              handleToggleTranscriptMarkers();
            }
          }}>
                  <>
                    {transcriptSrc && <DropdownSection title="Transcript" classNames={{
                base: 'p-0 m-0',
                heading: 'tool-heading',
                group: 'm-0 p-0'
              }}>
                        {transcriptAvailable ? <>
                            <DropdownItem key="transcript-visibility" shortcut="T" className="tool-item" classNames={{
                    base: 'p-0 m-0'
                  }} startContent={!showTranscript ? <SubtitlesIcon size={20} /> : <SubtitlesSlashIcon size={20} />}>
                              <span>{!showTranscript ? 'Show transcripts' : 'Hide transcripts'}</span>
                            </DropdownItem>
                            <DropdownItem key="transcript-position" shortcut="P" startContent={!changeTranscriptPosition || currentPosition === 'side' ? <SquareHalfBottomIcon size={20} /> : <SquareHalfIcon size={20} />} className='tool-item'>
                              <div className='flex flex-row whitespace-nowrap items-center gap-1 hover:text-gray-500'>
                                <span>Place</span>
                                <span className='font-semibold italic mr-0.5'>{!changeTranscriptPosition || currentPosition === 'side' ? 'below' : 'beside'}</span>
                                <span>player</span>
                              </div>
                            </DropdownItem>
                            <DropdownItem key="auto-scroll" shortcut="A" className="tool-item" startContent={<QueueIcon size={20} />}>
                              <div className='flex flex-row whitespace-nowrap items-center gap-1 hover:text-gray-500'>
                                <span>{autoScroll ? 'Disable' : 'Enable'} auto-scroll</span>
                              </div>
                            </DropdownItem>
                          </> : <DropdownItem key="transcript-visibility" className="tool-item" classNames={{
                  base: 'p-0 m-0'
                }} startContent={<SubtitlesSlashIcon size={20} />}>
                            <span>No transcript available</span>
                          </DropdownItem>}
                      </DropdownSection>}
                  </>
                  <>
                    {transcriptSrc && transcriptAvailable && <DropdownSection title="Track" className='mt-2' classNames={{
                base: 'p-0 mb-1',
                heading: 'tool-heading',
                group: 'm-0 p-0'
              }}>
                        <DropdownItem key="transcript-markers" shortcut="M" startContent={showTranscriptMarkersState === true ? <ChatCenteredSlashIcon size={20} /> : <ChatCenteredIcon size={20} />} className="tool-item">
                          <div className='flex flex-row whitespace-nowrap items-center gap-2'>
                            <span>{showTranscriptMarkersState === true ? 'Hide Markers' : 'Show Markers'}</span>
                          </div>
                        </DropdownItem>
                      </DropdownSection>}
                  </>
                  {(mediaItem.mediaType === 'video' || mediaItem.mediaType === 'audio') && isEditing ? <DropdownSection title="Time Indexes" className='mt-2' classNames={{
              base: 'p-0 mb-1',
              heading: 'tool-heading',
              group: 'm-0 p-0'
            }}>
                      {mediaItem.mediaType === 'video' && isEditing ? <DropdownItem key="still-frame" onPress={handleSetStillFrame} startContent={<CameraIcon size={20} />} className="tool-item group">
                          <div className='flex flex-row whitespace-nowrap items-center justify-between gap-2'>
                            <span>Set Still Frame</span>
                            <Tooltip content="Used in the timeline presentation navigation." placement="right" className="bg-gray-800 border-2 border-gray-200 rounded-lg text-white p-2">
                              <InfoIcon size={16} className="text-white/40 group-hover:text-gray-500 transition-colors duration-150" />
                            </Tooltip>
                          </div>
                        </DropdownItem> : null}
                      {(mediaItem.mediaType === 'video' || mediaItem.mediaType === 'audio') && isEditing ? <>
                          <DropdownItem key="edit-media" onPress={handleEditModeToggle} startContent={<FilmReelIcon size={20} />} shortcut="E" className="tool-item group">
                            <div className='flex flex-row whitespace-nowrap items-center justify-between gap-2'>
                              <span>{isEditingMediaContext ? 'Disable Editing' : 'Enable Editing'}</span>
                              <Tooltip content={isEditingMediaContext ? "Disables editing mode for this media item" : "Enables editing mode for this media item"} placement="right" className="bg-gray-800 border-2 border-gray-200 rounded-lg text-white p-2">
                                <InfoIcon size={16} className="text-white/40 group-hover:text-gray-500 transition-colors duration-150" />
                              </Tooltip>
                            </div>
                          </DropdownItem>
                        </> : null}
                    </DropdownSection> : null}
                </DropdownMenu>
              </Dropdown>}
          </>}
      </div>
    </div>;
};
export default Controls;